import { useCallback, useEffect, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { FormikProvider, useFormik } from 'formik'
import { DateTime } from 'luxon'
import { maxBy } from 'lodash'

import { useReselector, useScrollRef } from '@/utils/sharedHooks'
import { useAppDispatch } from '@/utils/typeHelpers'
import { DATE_FORMATS_LUXON } from '@/utils/dateHelpers'
import {
  ReferralStatus,
  updateUserReferralStatusIfExists,
} from '@/features/user/referrals/slice'
import { selectApproximatePracticeStartDate } from '@/selectors/catchupBookkeeping.selectors'
import { getCurrentUser, getFinancialProfile } from '@/selectors/user.selectors'
import { useSignupPageUpdate } from '@/services/onboardingService'
import { SIGNUP_PATHS } from '../helpers'
import { useFreeTrialEnabled } from '../useFreeTrialEnabled'
import { useAnalyticsTrack, useAnalyticsView } from '@/features/amplitude'
import {
  BecomeSCorpPopupContent,
  CommingledBankingPopupContent,
  PhysicalInventoryPopupContent,
} from './ToolTips'
import { getFieldNames, makeDateSchema, makeReqStringSchema } from '@/base'
import { BUSINESS_ENTITY_OPTIONS, TAXED_AS_TYPE } from './constants'
import { TypographyStyles } from '@/styles'
import { Form, Image, RadioGroupItem, Text } from '@/ui'
import { FreeTrialSignupBanner } from '../FreeTrialSignupBanner'
import { QuestionRow } from './QuestionRow'
import { PracticeType } from '@/constants/onboardingConstants'

import { fetchAllAnnualTaxDetailsIfNeeded } from '@/features/admin/annual-tax-details/annualTaxDetails.slice'
import { TAX_ENTITY_TYPES } from '@/features/taxes/taxConstants'
import { useSubmitYourPractice } from '@/features/user/signup/your-practice/helpers'
import { useNewCatalogSignup } from '@/features/user/signup/plans/useNewCatalogSignup'
import { SignupPageLayout } from '../SignupPageLayout'
import { SignupFooter } from '../SignupFooter'
import { SignupFormFieldsWrapper } from '../SignupFormFieldsWrapper'
import {
  BankCompatibility,
  TIER_COLOR_MAP,
  type BankAccountType,
} from '../about-you/BankCompatibility'
import {
  FiTier,
  parseInstitutionSelections,
  type AccountSubtype,
  type SelectedInstitution,
} from '../about-you/bank-compatibility.types'
import { useScrollToFieldError } from '@/utils/formHelpers'

/** Including self (so add 1) */
export enum NumberOfEmployees {
  justMe = '1',
  twoToTen = '2-10',
  tenOrMore = '10+',
}

const TIER_PRIORITY: Record<FiTier, number> = {
  [FiTier.NOT_SUPPORTED]: 2,
  [FiTier.LIMITED_SUPPORT]: 1,
  [FiTier.WELL_SUPPORTED]: 0,
}

export const YourPractice = () => {
  const dispatch = useAppDispatch()
  const pageView = useAnalyticsView()
  const track = useAnalyticsTrack()
  const bankCompatibilityViewedRef = useRef(false)
  const currentUser = useReselector(getCurrentUser)
  const financialProfile = useReselector(getFinancialProfile)
  const { scrollRef, scrollToRef } = useScrollRef({ autoScroll: true })
  const approximatePracticeStartDate = useReselector(
    selectApproximatePracticeStartDate
  )
  const submitYourPractice = useSubmitYourPractice()
  const catalogFlagPending = useNewCatalogSignup() === null
  const [searchParams] = useSearchParams()
  const freeTrialEnabled = useFreeTrialEnabled(searchParams.get('trial'))

  const [selectedInstitutions, setSelectedInstitutions] = useState<
    SelectedInstitution[]
  >(
    () =>
      parseInstitutionSelections(
        sessionStorage.getItem('signupInstitutionSelections')
      ) ?? []
  )
  useEffect(() => {
    dispatch(fetchAllAnnualTaxDetailsIfNeeded())
  }, [dispatch])

  const { email } = currentUser || {}
  useEffect(() => {
    if (email) {
      updateUserReferralStatusIfExists({
        recipientEmail: email,
        status: ReferralStatus.account_created,
      })(dispatch)
    }
  }, [email, dispatch])

  useEffect(() => {
    pageView('sign up about your practice')
  }, [pageView])

  useEffect(() => {
    scrollToRef()
  }, [scrollToRef])

  useSignupPageUpdate(SIGNUP_PATHS.aboutYourPractice)

  const formik = useFormik({
    initialValues: {
      taxEntityType: financialProfile?.taxEntityType as
        | TAX_ENTITY_TYPES
        | 'other',
      businessEntity: financialProfile?.businessEntity || undefined,
      accountsSeparate: financialProfile?.accountsSeparate?.toString(),
      willingAccountsSeparate:
        financialProfile?.willingAccountsSeparate?.toString(),
      hasMultipleOwners: null as boolean | null,
      scorpEntireYear: null as boolean | null,
      hasPhysicalInventory: null as boolean | null,
      fileInternationalTaxes:
        financialProfile?.fileInternationalTaxes?.toString(),
      approximatePracticeStartDate:
        approximatePracticeStartDate?.toFormat(DATE_FORMATS_LUXON.YEAR_MONTH) ||
        '',
      numberOfEmployees:
        financialProfile?.practiceType === PracticeType.solo
          ? NumberOfEmployees.justMe
          : financialProfile?.practiceType === PracticeType.group
            ? NumberOfEmployees.twoToTen
            : '',
    },
    validateOnMount: true,
    onSubmit: (values) => {
      const hasBusinessAccount = values.accountsSeparate === 'true'
      const hasPersonalAccount = values.accountsSeparate === 'false'

      track('answered practice account questions', {
        accountsSeparate: values.accountsSeparate ?? null,
        bank_compat_eligible: hasBusinessAccount || hasPersonalAccount,
      })

      if (bankCompatibilityViewedRef.current) {
        const accountType: BankAccountType =
          values.accountsSeparate === 'true'
            ? 'business'
            : 'personal_willing_to_separate'

        if (selectedInstitutions.length === 0) {
          track('skipped bank compatibility check', {
            bank_account_type: accountType,
          })
        }

        const worstInstitution = maxBy(
          selectedInstitutions,
          (inst) => TIER_PRIORITY[inst.tier]
        )
        const worstTier = worstInstitution?.tier ?? null

        track('bank compatibility page exit', {
          bank_compatibility_worst_result: worstTier
            ? TIER_COLOR_MAP[worstTier]
            : null,
          bank_compatibility_count: selectedInstitutions.length,
        })
      }

      return submitYourPractice({ ...values, selectedInstitutions })
    },
  })

  const { values, submitForm, isSubmitting } = formik
  const fieldNames = getFieldNames(formik)

  useScrollToFieldError(formik)

  const bankCompatibilityMountRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (node) {
        bankCompatibilityViewedRef.current = true
      }
    },
    []
  )

  // Keep selectedInstitutions' accountSubtype in sync when accountsSeparate changes
  useEffect(() => {
    setSelectedInstitutions((prev) => {
      if (!prev.length) return prev
      const subtype: AccountSubtype =
        values.accountsSeparate === 'false' ? 'personal' : 'business'
      const updated = prev.map((inst) => ({ ...inst, accountSubtype: subtype }))
      if (
        prev.every(
          (inst, i) => inst.accountSubtype === updated[i].accountSubtype
        )
      ) {
        return prev
      }
      return updated
    })
  }, [values.accountsSeparate])

  return (
    <SignupPageLayout
      maxWidth="wide"
      footer={
        <SignupFooter
          onContinue={submitForm}
          continueDisabled={isSubmitting || catalogFlagPending}
          continueLoading={isSubmitting}
          continueId="btn-signup-about-practice"
        />
      }
    >
      <FormikProvider value={formik}>
        <div className="flex flex-col gap-6">
          <span ref={scrollRef} />
          {freeTrialEnabled && <FreeTrialSignupBanner />}

          <Text variant={TypographyStyles.DISPLAY_MD}>
            Tell us about your practice
          </Text>

          <Text variant={TypographyStyles.BODY_MD}>
            To serve you accurately, we need a clear picture of your practice —
            please answer each question carefully.
          </Text>
          <div className="flex flex-row gap-14">
            <SignupFormFieldsWrapper>
              <div className="flex flex-col gap-8">
                <Form.RadioGroup
                  name={fieldNames.businessEntity}
                  schema={makeReqStringSchema({})}
                  label="How is your practice set up?"
                  aria-label="How is your practice set up?"
                  required
                >
                  {Object.entries(BUSINESS_ENTITY_OPTIONS).map(
                    ([value, label]) => (
                      <RadioGroupItem key={value} value={value} label={label} />
                    )
                  )}
                </Form.RadioGroup>

                <Form.RadioGroup
                  name={fieldNames.taxEntityType}
                  label="Your practice is taxed as a... "
                  aria-label="Your practice is taxed as a"
                  schema={makeReqStringSchema({})}
                >
                  {Object.entries(TAXED_AS_TYPE).map(([value, label]) => (
                    <RadioGroupItem key={value} value={value} label={label} />
                  ))}
                </Form.RadioGroup>

                {values.businessEntity === 'c_corp' &&
                  values.taxEntityType === 'form_1120_s' && (
                    <QuestionRow
                      name={fieldNames.scorpEntireYear}
                      label={`Has your practice been taxed as an S Corporation for the entirety of ${DateTime.now().year}?`}
                      popupContent={<BecomeSCorpPopupContent />}
                      schema={makeReqStringSchema()}
                    />
                  )}

                <Form.DateInput
                  name={fieldNames.approximatePracticeStartDate}
                  label="When did you start your practice?"
                  placeholder="Month"
                  dateFormat={DATE_FORMATS_LUXON.YEAR_MONTH}
                  showMonthYearPicker
                  maxDate={new Date()}
                  schema={makeDateSchema({
                    format: DATE_FORMATS_LUXON.YEAR_MONTH,
                  })}
                />

                <Form.RadioGroup
                  name={fieldNames.numberOfEmployees}
                  schema={makeReqStringSchema({})}
                  label="How many employees are in your practice?"
                  aria-label="How many employees are in your practice?"
                >
                  <RadioGroupItem
                    value={NumberOfEmployees.justMe}
                    label="Just me"
                  />
                  <RadioGroupItem
                    value={NumberOfEmployees.twoToTen}
                    label="I have between 1-9 employees and contractors."
                  />
                  <RadioGroupItem
                    value={NumberOfEmployees.tenOrMore}
                    label="I have 10 or more employees and contractors."
                  />
                </Form.RadioGroup>

                <div className="mt-6">
                  <Text variant={TypographyStyles.H1}>
                    Do any of these situations apply to you?
                  </Text>
                </div>

                <div className="flex flex-col">
                  <Form.Field
                    name={fieldNames.accountsSeparate}
                    schema={makeReqStringSchema()}
                    label="How do you pay for business expenses?"
                    popupContent={<CommingledBankingPopupContent />}
                  />
                  <div className="flex gap-2 mt-4">
                    <Form.Toggle
                      name={fieldNames.accountsSeparate}
                      value="true"
                      deselectValue=""
                      variant="outline"
                      size="lg"
                      className="min-w-[72px]"
                    >
                      Business bank account
                    </Form.Toggle>
                    <Form.Toggle
                      name={fieldNames.accountsSeparate}
                      value="false"
                      deselectValue=""
                      variant="outline"
                      size="lg"
                      className="min-w-[72px]"
                    >
                      Personal bank account
                    </Form.Toggle>
                  </div>
                </div>

                {values.accountsSeparate === 'true' && (
                  <div ref={bankCompatibilityMountRef}>
                    <BankCompatibility
                      selectedInstitutions={selectedInstitutions}
                      onInstitutionsChange={setSelectedInstitutions}
                      accountSubtype="business"
                      bankAccountType="business"
                    />
                  </div>
                )}

                {values.accountsSeparate === 'false' && (
                  <div ref={bankCompatibilityMountRef}>
                    <BankCompatibility
                      selectedInstitutions={selectedInstitutions}
                      onInstitutionsChange={setSelectedInstitutions}
                      accountSubtype="personal"
                      bankAccountType="personal_willing_to_separate"
                    />
                  </div>
                )}

                <QuestionRow
                  name={fieldNames.hasMultipleOwners}
                  label="Does your business have multiple owners?"
                  popupContent={
                    <Text variant={TypographyStyles.BODY_SM}>
                      e.g. business partner, co-owner, or anyone else who owns a
                      percentage of the practice
                    </Text>
                  }
                  schema={makeReqStringSchema()}
                />

                <QuestionRow
                  name={fieldNames.hasPhysicalInventory}
                  label="Does your practice keep physical inventory that you sell to clients?"
                  popupContent={<PhysicalInventoryPopupContent />}
                  schema={makeReqStringSchema()}
                />

                <QuestionRow
                  name={fieldNames.fileInternationalTaxes}
                  label="Does your practice need to pay international taxes?"
                  popupContent={
                    <Text variant={TypographyStyles.BODY_SM}>
                      You see international clients and your practice pays taxes
                      to countries other than the United States (including U.S.
                      territories).
                    </Text>
                  }
                  schema={makeReqStringSchema()}
                />
              </div>
            </SignupFormFieldsWrapper>
            <div className="w-[300px] shrink-0 self-start hidden md:block">
              <Image
                alt="couch"
                src="https://heard-images.s3.amazonaws.com/assets/couch.svg"
              />
            </div>
          </div>
        </div>
      </FormikProvider>
    </SignupPageLayout>
  )
}
